import { Component, OnInit, TemplateRef, inject, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { MatRadioChange, MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { PlannedPurpose } from 'src/app/core/models/purpose';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-add-kpi',
    template: `
    <h4 class="text-center my-3">{{ 'PLANNING.KPI.NEW' | translate }}</h4>
    <form [formGroup]="kpiForm" class="row">
      <mat-form-field appearance="outline" class="col-12">
        <mat-label>{{ 'PLANNING.ACTIONS.SELECT' | translate }}</mat-label>
        <mat-select formControlName="actionIndex">
          @for (action of actions; track action; let i = $index) {
            <mat-option [value]="i">{{ action.title }}</mat-option>
          }
        </mat-select>
        @if (actionIndex?.invalid) {
          <mat-error>{{ 'PLANNING.ACTIONS.SELECT_ERROR' | translate }}</mat-error>
        }
      </mat-form-field>
    
      <section class="mt-2 mb-4">
        <div class="mb-3">
          <label>{{ 'PLANNING.KPI.TYPE_SELECT' | translate }}</label>
        </div>
        <mat-radio-group formControlName="type" (change)="onKpiTypeChange($event)">
          <mat-radio-button [value]="1">{{ 'PLANNING.KPI.TYPE_QUANTITATIVE' | translate }}</mat-radio-button>
          <mat-radio-button class="ms-3" [value]="2">{{
            'PLANNING.KPI.TYPE_QUALITATIVE' | translate
          }}</mat-radio-button>
        </mat-radio-group>
      </section>
    
      <mat-form-field appearance="outline" class="col-12 mb-1">
        <mat-label>{{ 'PLANNING.KPI.NAME' | translate }}</mat-label>
        <input matInput formControlName="title" />
        @if (title?.invalid) {
          <mat-error>{{ 'PLANNING.KPI.NAME_ERROR' | translate }}</mat-error>
        }
      </mat-form-field>
    
      <mat-form-field appearance="outline" class="col-12 mb-1">
        <mat-label>{{ 'PLANNING.KPI.PROGRESS' | translate }}</mat-label>
        <input #progressRef mask="0999" (click)="progressRef.select()" matInput formControlName="progress" />
        @if (progress?.invalid) {
          <mat-error class="mb-2">
            @if (progress?.hasError('message')) {
              {{progress?.errors?.['message']}}
            } @else {
              {{ 'PLANNING.KPI.PROGRESS_ERROR' | translate }}
            }
          </mat-error>
        }
      </mat-form-field>
    
      <mat-form-field appearance="outline" class="col-12 mb-1">
        <mat-label>{{ 'PLANNING.KPI.NOTES' | translate }}</mat-label>
        <textarea matInput formControlName="description"></textarea>
        @if (description?.invalid) {
          <mat-error class="mb-2">{{ 'PLANNING.KPI.NOTES_ERROR' | translate }}</mat-error>
        }
      </mat-form-field>
    
      @if (kpiType === 1) {
        <mat-form-field appearance="outline" class="col-12 mb-1">
          <mat-label>{{ 'PLANNING.KPI.TARGET' | translate }}</mat-label>
          <input #targetRef (click)="targetRef.select()" matInput mask="09999" formControlName="target" />
          @if (target?.invalid) {
            <mat-error class="mb-2">{{ 'PLANNING.KPI.TARGET_ERROR' | translate }}</mat-error>
          }
        </mat-form-field>
      }
    </form>
    
    <ng-template #suggestedText let-description>
      <h2 class="text-center">{{ 'PLANNING.KPI.EDIT_INFO' | translate }}</h2>
      <p class="text-center" [innerHtml]="'PLANNING.KPI.ADD_EDIT_INFO' | translate"></p>
      <div class="d-flex justify-content-center mt-4">
        <button (click)="closeSuggestedText()" mat-flat-button color="accent">{{ 'CLOSE' | translate }}</button>
      </div>
    </ng-template>
    
    <section class="d-flex justify-content-center my-3">
      <button
        mat-icon-button
        [disableRipple]="true"
        [matTooltip]="'PLANNING.KPI.SHOW_INFO' | translate"
        color="accent"
        (click)="displaySuggestedText()"
        >
        <mat-icon>info</mat-icon>
      </button>
      <button mat-flat-button mat-dialog-close>{{ 'CANCEL' | translate }}</button>
      <button mat-flat-button color="accent" [mat-dialog-close]="formValue" [disabled]="!kpiForm.valid">
        {{ 'CONFIRM' | translate }}
      </button>
    </section>
    `,
    styles: [':host { display: block; max-width: 500px}'],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOption, MatError, MatRadioGroup, MatRadioButton, MatInput, MatButton, MatIconButton, MatTooltip, MatIcon, MatDialogClose, TranslateModule]
})
export class AddKpiDialogComponent implements OnInit {
  private dialog = inject(MatDialog);
  private fb = inject(UntypedFormBuilder);
  actions = inject(MAT_DIALOG_DATA);

  kpiForm!: UntypedFormGroup;
  suggestedTextDialogRef!: MatDialogRef<any>;

  readonly suggestedTextTpl = viewChild.required<TemplateRef<any>>('suggestedText');

  targetControl = new UntypedFormControl(null, [Validators.required, Validators.min(1)]);

  ngOnInit(): void {
    this.kpiForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      progress: [0, Validators.required],
      type: [1, Validators.required],
      actionIndex: [null, Validators.required],
      target: [1, [Validators.required, Validators.min(1)]],
    });
  }

  get formValue(): {
    actionIndex: string;
    kpi: PlannedPurpose.QualitativeKpi | PlannedPurpose.QuantitativeKpi;
  } {
    const { type, actionIndex, ...kpi } = this.kpiForm.getRawValue();
    if (type === PlannedPurpose.KpiType.QUANTITATIVE) {
      return {
        actionIndex,
        kpi: new PlannedPurpose.QuantitativeKpi(
          kpi.title,
          kpi.description,
          parseInt(kpi.progress),
          parseInt(kpi.target)
        ),
      };
    } else {
      return {
        actionIndex,
        kpi: new PlannedPurpose.QualitativeKpi(kpi.title, kpi.description, kpi.progress),
      };
    }
  }

  /* 
    1 -> QuantitativeKpi
    2 -> Qualitative
  */
  get kpiType(): 1 | 2 {
    return this.kpiForm.get('type')?.value;
  }

  get progress() {
    return this.kpiForm.get('progress');
  }

  get target() {
    return this.kpiForm.get('target');
  }

  get title() {
    return this.kpiForm.get('title');
  }

  get description() {
    return this.kpiForm.get('description');
  }

  get actionIndex() {
    return this.kpiForm.get('actionIndex');
  }

  onKpiTypeChange(type: MatRadioChange) {
    if (type.value === PlannedPurpose.KpiType.QUANTITATIVE) {
      this.kpiForm.removeControl('target');
      this.kpiForm.addControl('target', this.targetControl);
      this.target?.addValidators(Validators.required);
    } else {
      this.kpiForm.removeControl('target');
    }
  }

  displaySuggestedText() {
    this.suggestedTextDialogRef = this.dialog.open(this.suggestedTextTpl(), {
      panelClass: 'col-md-5',
    });
  }

  closeSuggestedText() {
    this.suggestedTextDialogRef?.close();
  }
}
